import styled from '@emotion/styled'
import { withClass } from 'common/emotion'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import { breakWords } from 'component-library/__styling/style-mixins'
import { colors, metrics } from 'component-library/__styling/settings'
import { breakpoint, calcRem } from 'component-library/__styling/style-functions'
import { Theme } from 'component-library/__styling/themes'

import { CardMedia } from 'component-library/cards/CardMedia/CardMedia'
import { StyledLink as CardTextInteractions } from 'component-library/cards/CardText/CardTextInteractions.styled'
import { CardText } from 'component-library/cards/CardText/CardText'
import { themedValue } from 'component-library/__styling/themed-value'
import { styledMediaContentClassName } from '../CardMedia/StyledMedia.styled'
import { HeroVideoLayoutRatios } from '../HeroVideoCard/HeroVideoCard'

function layoutRatioToValue(ratio: HeroVideoLayoutRatios): any {
    switch (ratio) {
        case '3:1':
            return ['66.666666%', '33.333333%']
        case '1:3':
            return ['33.333333%', '66.666666%']
        default:
            return ['50%', '50%']
    }
}

export const styledHeroClassName = 'HeroCard'

export interface StyledHeroCardProps {
    className?: string
    isSponsored: boolean | null
    hasBackground?: boolean
    primaryTopic: string | undefined
    theme: Theme
}

export const StyledHeroCard = withClass(styledHeroClassName)(
    styled('div')<StyledHeroCardProps>(
        {
            ...breakWords,
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            flexGrow: 1,
        },
        props => ({
            borderBottom: themedValue(props.theme, {
                thewest: `1px solid ${colors.thewest.greyDolphin}`,
                perthnow: `1px solid ${colors.perthnow.greyMarble}`,
                sevennews: undefined,
            }),
            ...conditionalStyles(props.theme.kind === 'perthnow', {
                borderBottom: props.isSponsored
                    ? `2px solid ${colors.black}`
                    : `1px solid ${colors.perthnow.greyMarble}`,
            }),
            backgroundColor: themedValue(props.theme, {
                perthnow: props.hasBackground ? colors.perthnow.greyBasalt : colors.white,
                thewest: props.hasBackground ? colors.white : 'transparent',
                sevennews: props.hasBackground ? colors.white : 'transparent',
            }),
            alignItems: themedValue(props.theme, {
                perthnow: 'flex-start',
                thewest: 'stretch',
                sevennews: 'stretch',
            }),
        })
    )
)
StyledHeroCard.displayName = 'StyledHeroCard'

export interface StyledCellProps {
    layoutRatio: HeroVideoLayoutRatios
}

export const StyledCell = styled('div')<StyledCellProps>((props: StyledCellProps) => {
    const [col1Dimensions, col2Dimensions] = layoutRatioToValue(props.layoutRatio)

    return {
        width: '100%',

        [breakpoint('sm')]: {
            ':nth-child(1)': {
                display: 'flex',
                width: props.layoutRatio !== '1:1' ? col1Dimensions : '45%',

                [`${CardTextContainer}`]: {
                    width: '100%',
                },
            },
            ':nth-child(2)': {
                width: props.layoutRatio !== '1:1' ? col2Dimensions : '55%',

                [`${StyledHeroCardMedia}`]: {
                    width: '100%',
                },
            },
        },
    }
})

export const CardTextContainer = styled('div')(
    {
        width: '100%',
        flex: 'none',
        fontSize: calcRem(15),

        [breakpoint('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            width: '45%',
        },
    },
    props => ({
        padding: props.theme.cards.outerMargin,
        paddingLeft: themedValue(props.theme, {
            thewest: 0,
            fallback: props.theme.margins.md,
        }),
        paddingRight: props.theme.margins.md,
    })
)
CardTextContainer.displayName = 'CardTextContainer'

export const StyledHeroCardMedia = styled(CardMedia)(
    {
        width: '100%',
        display: 'block',
        alignSelf: 'flex-start',

        [`& .${styledMediaContentClassName}`]: {
            '& picture': {
                display: 'block',
                // Not needed because of no lazy load
                // Without this, when JS is disabled the hero card
                // doubles in height
                '& noscript': { display: 'none' },
            },
        },

        [breakpoint('sm')]: {
            [`& .${styledMediaContentClassName}`]: {
                '& picture': {
                    // We don't want placeholder for this breakpoint
                    '&::after': {
                        display: 'none',
                    },
                    '& img': {
                        position: 'static',
                    },
                },
            },
            width: '55%',
            marginTop: 0,
        },
    },
    props => ({
        marginTop: themedValue(props.theme, {
            thewest: calcRem(props.theme.margins.sm),
            sevennews: calcRem(metrics.sevennews.margins.sm),
            fallback: undefined,
        }),

        // Object fit style, crops to fill vertical and horizontal space
        ...conditionalStyles(props.imageLayout === 'none', {
            [`& .${styledMediaContentClassName}`]: {
                '& picture': {
                    '& img': {
                        objectFit: 'cover',
                        width: '100%',
                        flexGrow: 0,
                    },
                },
            },
        }),
    })
)
StyledHeroCardMedia.displayName = 'StyledHeroCardMedia'

export const StyledLink = styled(CardTextInteractions)({
    textDecoration: 'none',
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
})
StyledLink.displayName = 'StyledLink'

export const StyledHeroCardText = styled(CardText)(
    {
        paddingTop: 0,
    },
    ({ theme }) => ({
        '& .Card-HeadlineText': {
            fontWeight: themedValue(theme, {
                thewest: 400,
                fallback: undefined,
            }),
        },
    })
)
StyledHeroCardText.displayName = 'StyledHeroCardText'
